import { useCallback, useMemo, useState } from 'react'
import type { TextFieldProps } from '@mui/material'
import useFavurTranslation from 'hooks/useFavurTranslation'
import useJamesApolloMutation from 'hooks/useJamesApolloMutation'
import type { AcceptAbsenceRequestFieldsT } from 'pages/Tasks/AbsenceRequest/Details/Accept/types'
import { updateAbsenceRequestMutationApollo } from 'pages/Tasks/AbsenceRequest/mutations'
import type { SwitchProps } from 'redesign/components/atoms/Switch/types'
import type { SelectProps } from 'redesign/components/atoms/select/types'
import { SNACKBAR_VARIANTS } from 'redesign/components/atoms/snackbar-base/constants'
import { useToastContext } from 'redesign/services/toast/use-toast-context'
import { formatDate } from 'redesign/shared/date'
import { DATE_FORMAT_VARIANTS } from 'redesign/shared/date/constants'
import { LARGE_TEXT_FIELD_MIN_ROWS, ONE, ZERO } from 'shared/constants'
import type { ApproveAbsenceRequestResultT } from 'types'
import { newDateWithoutTime } from 'utils/date'
import { TASKS_STATUS } from '../ContentLists/TaskList/constants'
import {
  ABSENCE_TYPE_SELECT_TEST_ID,
  DIFF_DAYS_SWITCH_INPUT_TEST_ID,
  INDIVIDUAL_ABSENCE_TYPE_SELECT_TEST_ID,
  MAX_LENGTH_INTERNAL_COMMENT,
  MAX_LENGTH_REVIEWER_COMMENT,
  MAX_MIRUS_COMMENT_ROWS,
  MAX_TASK_COMMENT_ROWS,
  MIRUS_COMMENT_INPUT_TEST_ID,
  TASK_COMMENT_INPUT_TEST_ID,
} from './constants'
import type { AbsenceDate, IndividualAbsenceTypeSelectProps, UseAcceptFormProps } from './types'
import { getAbsenceTypesOptions } from './utils/get-absence-type-options'
import { getTaskDatesInitialData } from './utils/get-task-dates-initial-data'
import { resolveTaskDates } from './utils/resolve-task-dates'

export const useAcceptForm = ({ task, onAccept }: UseAcceptFormProps) => {
  const { t, locale } = useFavurTranslation()
  const [approveAbsenceRequest] = useJamesApolloMutation<ApproveAbsenceRequestResultT>(
    updateAbsenceRequestMutationApollo,
  )
  const { openToast } = useToastContext()

  const taskDates = resolveTaskDates(task)
  const isSingleDay = taskDates.length <= ONE
  const emptyErrorsArray = useMemo(() => Array(taskDates.length).fill(''), [taskDates.length])
  const [absenceType, setAbsenceType] = useState<string>('')
  const [absenceErrors, setAbsenceErrors] = useState<string[]>(emptyErrorsArray)
  const [datesData, setDatesData] = useState<AbsenceDate[]>(getTaskDatesInitialData(taskDates))
  const [reviewerComment, setReviewerComment] = useState<string>()
  const [internalComment, setInternalComment] = useState<string>()
  const [isSameForAllDays, setIsSameForAllDays] = useState<boolean>(true)
  const [loading, setLoading] = useState(false)

  const resetErrorArray = useCallback(() => setAbsenceErrors(emptyErrorsArray), [emptyErrorsArray])

  const handleAbsenceTypeForAll = useCallback(
    (newType: string) => {
      const arrayModified = datesData.map((item) => {
        const value = { ...item, type: newType }

        return value
      })

      setDatesData(arrayModified)
      Boolean(newType) && resetErrorArray()
    },
    [datesData, resetErrorArray],
  )

  const handleTypeChange = useCallback(
    (newType: string, index: number) => {
      const { [index]: item, ...rest } = datesData
      const value = { ...item, type: newType }
      const modified = Object.values({ [index]: value, ...rest }) as AbsenceDate[]

      setDatesData(modified)
    },
    [datesData],
  )

  const handleAbsenceTypeToggle = useCallback(() => {
    const newIndex = !isSameForAllDays

    setIsSameForAllDays(newIndex)

    // If we move the toggle to same, we reset the individual values
    if (!newIndex) {
      handleAbsenceTypeForAll(absenceType)
    }
  }, [absenceType, handleAbsenceTypeForAll, isSameForAllDays])

  const resetErrorIndex = useCallback(
    (value: string, index: number) => {
      const newErrorsArray = [...absenceErrors]

      if (value) {
        newErrorsArray[index] = ''
        setAbsenceErrors(newErrorsArray)
      }
    },
    [absenceErrors],
  )

  const checkIsDataValid = useCallback(
    (data: AcceptAbsenceRequestFieldsT) => {
      const requiredError = t('common.error.required')

      const newErrorsArray = data.datesData.map((value) => {
        if (!value.type) {
          return requiredError
        }

        return ''
      })

      setAbsenceErrors(newErrorsArray)

      return !newErrorsArray.find((value) => {
        return Boolean(value)
      })
    },
    [t],
  )

  const onSubmit = useCallback(async () => {
    const data = { datesData, reviewerComment, internalComment }

    if (!checkIsDataValid(data)) {
      return
    }

    setLoading(true)

    await approveAbsenceRequest({
      variables: {
        dates: data.datesData, //getFrom the form
        internalComment: data.internalComment ?? '',
        reviewerComment: data.reviewerComment ?? '',
        status: TASKS_STATUS.approvedOffice,
        taskUuid: task.favurUuid,
      },
    })
      .then(() => {
        setLoading(false)
        openToast({ title: t('confirmation.answerSent'), variant: SNACKBAR_VARIANTS.success })
        onAccept()
      })
      .catch((error) => {
        setLoading(false)
        openToast({
          title: t(error.message),
          variant: SNACKBAR_VARIANTS.error,
        })
      })
  }, [
    datesData,
    reviewerComment,
    internalComment,
    checkIsDataValid,
    approveAbsenceRequest,
    task.favurUuid,
    onAccept,
    t,
    openToast,
  ])

  const diffDaysSwitchProps = useMemo<SwitchProps>(
    () => ({
      checked: isSameForAllDays,
      dataTestId: DIFF_DAYS_SWITCH_INPUT_TEST_ID,
      onChange: handleAbsenceTypeToggle,
      label: t('absenceType.sameForAll'),
    }),
    [handleAbsenceTypeToggle, isSameForAllDays, t],
  )

  const absenceTypeSelectProps = useMemo<SelectProps>(
    () => ({
      label: t('absenceRequest.accept.absenceType.label'),
      value: absenceType,
      onChange: (value) => {
        setAbsenceType(value ?? '')
        handleAbsenceTypeForAll(value ?? '')
      },
      options: getAbsenceTypesOptions(t),
      fullWidth: true,
      error: absenceErrors[ZERO],
      testId: ABSENCE_TYPE_SELECT_TEST_ID,
    }),
    [absenceErrors, absenceType, handleAbsenceTypeForAll, t],
  )

  const individualAbsenceTypeFieldsProps = useMemo<IndividualAbsenceTypeSelectProps[]>(
    () =>
      datesData.map((value, index) => ({
        selectTitle: formatDate({
          date: newDateWithoutTime(value.date),
          dateFormat: DATE_FORMAT_VARIANTS.shortWithWeekday,
          t,
          locale,
        }),
        key: `individual-absence-type-${value.date}`,
        fullWidth: true,
        label: t('absenceRequest.accept.absenceType.label'),
        options: getAbsenceTypesOptions(t),
        value: value.type,
        onChange: (newType) => {
          resetErrorIndex(newType ?? '', index)
          handleTypeChange(newType ?? '', index)
        },
        error: absenceErrors[index],
        testId: `${INDIVIDUAL_ABSENCE_TYPE_SELECT_TEST_ID}-${index}`,
      })),
    [absenceErrors, datesData, handleTypeChange, locale, resetErrorIndex, t],
  )

  const taskCommentFieldProps = useMemo<TextFieldProps>(
    () => ({
      name: 'reviewerComment',
      fullWidth: true,
      maxRows: MAX_TASK_COMMENT_ROWS,
      minRows: LARGE_TEXT_FIELD_MIN_ROWS,
      multiline: true,
      label: t('absenceRequest.accept.reviewerComment.label'),
      onChange: (e) => setReviewerComment(e.target.value),
      slotProps: {
        inputLabel: {
          shrink: true,
        },
        htmlInput: { maxLength: MAX_LENGTH_REVIEWER_COMMENT, 'data-testid': TASK_COMMENT_INPUT_TEST_ID },
      },
    }),
    [t],
  )

  const mirusCommentFieldProps = useMemo<TextFieldProps>(
    () => ({
      name: 'internalComment',
      fullWidth: true,
      maxRows: MAX_MIRUS_COMMENT_ROWS,
      minRows: LARGE_TEXT_FIELD_MIN_ROWS,
      multiline: true,
      label: t('absenceRequest.accept.internalComment.label'),
      onChange: (e) => setInternalComment(e.target.value),
      slotProps: {
        inputLabel: {
          shrink: true,
        },
        htmlInput: { maxLength: MAX_LENGTH_INTERNAL_COMMENT, 'data-testid': MIRUS_COMMENT_INPUT_TEST_ID },
      },
    }),
    [t],
  )

  return {
    loading,
    onSubmit,
    showDiffDaysToggle: !isSingleDay,
    diffDaysSwitchProps,
    absenceTypeSelectProps,
    showSingleTypeField: isSameForAllDays,
    taskCommentFieldProps,
    mirusCommentFieldProps,
    individualAbsenceTypeFieldsProps,
    t,
  }
}
